import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useEffect, useCallback, useState, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  donationMainCategoryMasterAPI,
  type DonationMainCategoryMaster,
  type DonationMainCategoryMasterCreatePayload,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

type DonationMainCategoryFormState = {
  name: string;
  is_active: boolean;
};

const initialFormState: DonationMainCategoryFormState = {
  name: "",
  is_active: true,
};

function getErrorMessage(
  err: unknown,
  fallback: string = "Failed to save donation main category"
) {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as {
        response?: {
          data?: { message?: string; detail?: string; error?: string };
        };
      }
    ).response;
    return (
      response?.data?.message ??
      response?.data?.detail ??
      response?.data?.error ??
      (err as unknown as Error)?.message ??
      fallback
    );
  }
  return (err as Error)?.message ?? fallback;
}

type DonationMainCategoryPageProps = {
  embedded?: boolean;
};

export default function DonationMainCategoryPage({
  embedded = false,
}: DonationMainCategoryPageProps) {
  const [donationMainCategories, setDonationMainCategories] = useState<
    DonationMainCategoryMaster[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] =
    useState<DonationMainCategoryFormState>(initialFormState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<DonationMainCategoryMaster | null>(
    null
  );
  const [deleting, setDeleting] = useState<DonationMainCategoryMaster | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const resetForm = () => {
    setForm(initialFormState);
    setEditing(null);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await donationMainCategoryMasterAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setDonationMainCategories(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load donation main categories");
      setDonationMainCategories([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Title / Name is required");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload: DonationMainCategoryMasterCreatePayload = {
        name: form.name.trim(),
        is_active: form.is_active,
      };

      if (editing) {
        await donationMainCategoryMasterAPI.update(editing.id, payload);
        toast.success("Donation main category updated successfully");
      } else {
        await donationMainCategoryMasterAPI.create(payload);
        toast.success("Donation main category created successfully");
      }

      setDialogOpen(false);
      resetForm();
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      toast.error(String(getErrorMessage(err)));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }

    setDeleteLoading(true);
    try {
      await donationMainCategoryMasterAPI.delete(deleting.id);
      toast.success("Donation main category deleted successfully");
      setDeleting(null);
      if (donationMainCategories.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      toast.error(
        String(getErrorMessage(err, "Failed to delete donation main category"))
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = useCallback(
    (donationMainCategory: DonationMainCategoryMaster) => {
      setEditing(donationMainCategory);
      setForm({
        name: donationMainCategory.name,
        is_active: donationMainCategory.is_active ?? true,
      });
      setDialogOpen(true);
    },
    []
  );

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<DonationMainCategoryMaster>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        nameColumnLabel: "Title / Name",
      }),
    [currentPage, pageSize, openEdit]
  );

  return (
    <div className={embedded ? "" : "p-6"}>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground">
            Donation Main Category
          </h1>
          <p className="text-muted-foreground">
            Manage donation main categories used in donation records
          </p>
        </div>
      )}
      <ListMasterPageLayout
        tableId="list-master-donation-main-category"
        tableColumns={tableColumns}
        listTitle={`Donation Main Categories (${totalItems})`}
        searchPlaceholder="Search donation main categories..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing
                    ? "Edit Donation Main Category"
                    : "Create New Donation Main Category"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="donation-main-category-name">
                    Title / Name *
                  </Label>
                  <Input
                    id="donation-main-category-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Enter title or name"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                  <div>
                    <Label htmlFor="donation-main-category-active">
                      Status
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {form.is_active ? "Active" : "Inactive"}
                    </p>
                  </div>
                  <Switch
                    id="donation-main-category-active"
                    checked={form.is_active}
                    onCheckedChange={checked =>
                      setForm(prev => ({ ...prev, is_active: checked }))
                    }
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <CustomLoader size="xs" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-donation-main-category"
            columns={tableColumns}
            data={donationMainCategories}
            getRowKey={d => d.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No donation main categories yet. Create one to get started."
                : "No donation main categories found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Donation Main Category</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
